import { useEffect, useRef } from 'react';
import { setSentryUser } from '@/lib/sentry';
import { useUserProfile } from './useUserProfile';
import { useActiveMode, useUser } from '@/stores/authStore';
import { logger } from '@/lib/logger';

/**
 * Hook que sincroniza o contexto do usuário com o Sentry
 * Atualiza automaticamente quando o usuário faz login/logout ou troca de perfil
 * 
 * 🏗️ FASE 0: Migrado para usar Zustand diretamente
 */
export const useSentryUser = () => {
  const user = useUser();
  const { data: userProfile } = useUserProfile(user?.id || null);
  const activeMode = useActiveMode();
  const lastFingerprintRef = useRef<string | null>(null);
  
  useEffect(() => {
    try {
      if (!user) {
        lastFingerprintRef.current = null;
        setSentryUser(null);
        return;
      }

      const payload = {
        userId: user.id,
        name: userProfile?.profile?.name ?? null,
        email: user.email ?? null,
        activeMode: activeMode ?? null,
      };

      const fingerprint = JSON.stringify(payload);
      if (lastFingerprintRef.current === fingerprint) return;
      lastFingerprintRef.current = fingerprint;

      setSentryUser(user.id, {
        name: userProfile?.profile?.name,
        email: user.email,
        activeMode,
      });
    } catch (err) {
      logger.error('[useSentryUser] Error updating context:', err instanceof Error ? err : String(err));
      // Não propagar erro - Sentry é auxiliar, não crítico
    }
  }, [user, userProfile?.profile?.name, user?.email, activeMode]);
};
